import {
  Configuration,
  type PlacementMinBid,
  PlacementsApi,
} from '@campaign-manager/rmt-campaign-service';
import {
  customPlacementRequestConstraints,
  displayPlacementRequestConstraints,
  dOOHPlacementRequestConstraints,
  nativePlacementRequestConstraints,
  placementPatchUpdateConstraints,
  placementUpdateConstraints,
  sPAPlacementRequestConstraints,
} from '@campaign-manager/rmt-campaign-service/src/constraints';

import { getUrlWithQuery } from '../../../../infra/repo/rmt-api/getUrlWithQuery';
import {
  PlacementStatus,
  type RawPlacementApiCreateRequest,
  type RawPlacementApiResponse,
  type RawPlacementApiUpdateRequest,
  rawPageInfoResponseToPageInfo,
} from '../../../../model';
import { normalized } from '../../../api-middleware/createNormalizingProxy';
import type { ApiCallerOptions } from '../ApiCaller';
import type { ApiCallerResult } from '../ApiCallerResult';

export class PlacementRepo {
  private api: PlacementsApi;

  constructor() {
    this.api = PlacementRepo._api();
  }

  private static _api() {
    const basePath = '/api/rmt/v1';

    const config = new Configuration({
      basePath,
      baseOptions: {
        headers: {
          'cache-control': 'no-store, no-cache',
        },
      },
    });
    return new PlacementsApi(config);
  }

  async listPlacements(
    options: ApiCallerOptions,
  ): Promise<ApiCallerResult<RawPlacementApiResponse[]>> {
    const res = await this.api.listPlacements(
      options.query?.filter,
      options.query?.orderBy,
      options.query?.limit,
      options.query?.cursor,
    );

    return {
      data: res.data.data as RawPlacementApiResponse[],
      pageInfo: rawPageInfoResponseToPageInfo(res.data.page_info),
    };
  }

  static listPlacementsCallKey(options: ApiCallerOptions) {
    return getUrlWithQuery('/placements', options.query);
  }

  async getPlacement(options: ApiCallerOptions): Promise<RawPlacementApiResponse> {
    const id = options.path?.id as string;
    const res = await this.api.getPlacement(id);

    return res.data.data as RawPlacementApiResponse;
  }

  static getPlacementCallKey(options: ApiCallerOptions) {
    const id = options.path?.id as string;
    return getUrlWithQuery(`/placements/${id}`, options.query);
  }

  async createPlacement(data: RawPlacementApiCreateRequest): Promise<RawPlacementApiResponse> {
    const constraints = getPlacementRequestConstraints((data as any).type);
    const res = await this.api.createPlacement(normalized(data, constraints) as any);
    return res.data.data as RawPlacementApiResponse;
  }

  async updatePlacement(id: string, data: RawPlacementApiUpdateRequest) {
    await this.api.updatePlacement(id, normalized(data, placementUpdateConstraints));
  }

  async archivePlacement(id: string) {
    await this.api.archivePlacement(id);
  }

  async activatePlacement(id: string) {
    await this.api.patchUpdatePlacement(
      id,
      normalized(
        {
          status: PlacementStatus.ACTIVE,
        },
        placementPatchUpdateConstraints,
      ),
    );
  }

  async deactivatePlacement(id: string) {
    await this.api.patchUpdatePlacement(
      id,
      normalized(
        {
          status: PlacementStatus.INACTIVE,
        },
        placementPatchUpdateConstraints,
      ),
    );
  }

  async unpublishPlacement(id: string) {
    await this.api.patchUpdatePlacement(
      id,
      normalized(
        {
          status: PlacementStatus.DRAFT,
        },
        placementPatchUpdateConstraints,
      ),
    );
  }

  async listPlacementKeywords(options: ApiCallerOptions): Promise<ApiCallerResult<string[]>> {
    const id = options.path?.id as string;
    const res = await this.api.getPlacementKeywords(
      id,
      (options.query?.q as string | undefined) ?? undefined,
      options.query?.limit,
      options.query?.cursor,
    );

    return {
      data: (res.data.data as string[]) || [],
      pageInfo: rawPageInfoResponseToPageInfo(res.data.page_info),
    };
  }

  static listPlacementKeywordsKey(options: ApiCallerOptions) {
    const id = options.path?.id as string;
    return getUrlWithQuery(`/placements/${id}/keywords`, options.query);
  }

  async listPlacementKeywordSuggestions(
    options: ApiCallerOptions,
  ): Promise<ApiCallerResult<string[]>> {
    const id = options.path?.id as string;
    const res = await this.api.getPlacementKeywordSuggestions(id, options.query?.request);

    return {
      data: (res.data.data as string[]) || [],
      pageInfo: {},
    };
  }

  static listPlacementKeywordSuggestionsKey(options: ApiCallerOptions) {
    const id = options.path?.id as string;
    return getUrlWithQuery(`/placements/${id}/keywords/suggestions`);
  }

  async getPlacementMinBidConstraint(options: ApiCallerOptions): Promise<PlacementMinBid> {
    const id = options.path?.id as string;
    const res = await this.api.getMinBid(id, options.query?.request);

    return res.data.data as PlacementMinBid;
  }

  static getPlacementMinBidConstraintCallKey(options: ApiCallerOptions) {
    const id = options.path?.id as string;
    return getUrlWithQuery(`/placements/${id}/constraints/min-bid`, options.query);
  }
}

function getPlacementRequestConstraints(type: string) {
  switch (type) {
    case 'SPA':
      return sPAPlacementRequestConstraints;
    case 'DISPLAY':
      return displayPlacementRequestConstraints;
    case 'NATIVE':
      return nativePlacementRequestConstraints;
    case 'DOOH':
      return dOOHPlacementRequestConstraints;
    case 'CUSTOM':
      return customPlacementRequestConstraints;
    default:
      return sPAPlacementRequestConstraints;
  }
}
